// src/components/FieldHistoryPanel.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { apiFetch, safeReadError } from "../api/api.js";

/** -------- helpers -------- */
function pad2(n) {
  return String(n).padStart(2, "0");
}

function fmtDateUA(d) {
  if (!d) return "—";
  const s = String(d);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, dd] = s.split("-");
    return `${dd}.${m}.${y}`;
  }
  const dt = new Date(s);
  if (!Number.isFinite(dt.getTime())) return s;
  return `${pad2(dt.getDate())}.${pad2(dt.getMonth() + 1)}.${dt.getFullYear()}`;
}

function fmtDateTimeUA(dt) {
  if (!dt) return "—";
  const d = new Date(String(dt));
  if (!Number.isFinite(d.getTime())) return String(dt);
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}, ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function clampStr(x, max = 240) {
  const s = String(x ?? "").trim();
  return s.length > max ? s.slice(0, max) : s;
}

function safeJsonParse(s) {
  if (!s) return null;
  try {
    if (typeof s === "object") return s;
    return JSON.parse(String(s));
  } catch {
    return null;
  }
}

/** ---- stable "last load wins" ---- */
function useSeq() {
  const seqRef = useRef(0);
  const next = () => {
    seqRef.current += 1;
    return seqRef.current;
  };
  const isLatest = (seq) => seq === seqRef.current;
  return { next, isLatest };
}

/** -------- dictionaries -------- */

// Типи подій (під UI)
const TYPE_LABELS = [
  { value: "STATUS_CHANGE", label: "Зміна статусу" },
  { value: "NOTE", label: "Нотатка" },
  { value: "PLANTING", label: "Посів" },
  { value: "SOWING", label: "Посів" }, // legacy
  { value: "SPRAYING", label: "Обробка" },
  { value: "FERTILIZING", label: "Добрива" },
  { value: "FERTILIZATION", label: "Добрива" },
  { value: "IRRIGATION", label: "Полив" },
  { value: "HARVEST", label: "Збір урожаю" },
  { value: "OTHER", label: "Інше" }
];

function labelFrom(list, v) {
  const s = String(v || "");
  const f = list.find((x) => x.value === s);
  return f ? f.label : s || "—";
}

function toneForType(type) {
  const t = String(type || "");
  if (t === "STATUS_CHANGE") return "info";
  if (t === "PLANTING" || t === "SOWING") return "ok";
  if (t === "SPRAYING" || t === "FERTILIZING" || t === "FERTILIZATION") return "warn";
  if (t === "HARVEST") return "done";
  if (t === "NOTE") return "muted";
  return "muted";
}

// ✅ Переклад статусів сезону (як у FieldDetail)
const SEASON_FIELD_OPTIONS = [
  { value: "PLANNED", label: "Заплановано" },
  { value: "PREPARATION", label: "Підготовка" },
  { value: "PLANTED", label: "Посіяно / Посаджено" },
  { value: "EMERGED", label: "Сходи" },
  { value: "GROWING", label: "Вегетація" },
  { value: "FLOWERING", label: "Цвітіння" },
  { value: "MATURING", label: "Дозрівання" },
  { value: "READY_TO_HARVEST", label: "Готове до збору" },
  { value: "HARVESTED", label: "Зібрано" },
  { value: "COMPLETED", label: "Завершено" },
  { value: "FALLOW", label: "Пар / Без культури" }
];

function seasonFieldLabel(enumVal) {
  const v = String(enumVal || "");
  const found = SEASON_FIELD_OPTIONS.find((x) => x.value === v);
  return found ? found.label : v || "—";
}

function statusTone(v) {
  const s = String(v || "");
  if (s === "COMPLETED") return "done";
  if (s === "HARVESTED" || s === "READY_TO_HARVEST") return "warn";
  if (s === "FLOWERING" || s === "MATURING") return "info";
  if (s === "GROWING" || s === "EMERGED") return "ok";
  if (s === "PLANTED" || s === "PREPARATION") return "ok2";
  if (s === "FALLOW" || s === "PLANNED") return "muted";
  return "muted";
}

function humanActor(ev) {
  const name = String(ev?.createdByName ?? "").trim();
  const email = String(ev?.createdByEmail ?? "").trim();
  const id = ev?.createdById ?? ev?.createdBy ?? ev?.createdByUserId;

  if (name && email) return `${name} (${email})`;
  if (name) return name;
  if (email) return email;
  if (id != null) return `User #${id}`;
  return "—";
}

/** sort key (newest first) */
function getEventAt(ev) {
  const createdAt = ev?.createdAt ? new Date(ev.createdAt).getTime() : 0;
  const eventDate = ev?.eventDate ? new Date(ev.eventDate).getTime() : 0;
  return createdAt || eventDate || 0;
}

function groupKeyByDate(ev) {
  const d = ev?.eventDate || ev?.createdAt;
  if (!d) return "unknown";
  const s = String(d);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const dt = new Date(s);
  if (!Number.isFinite(dt.getTime())) return "unknown";
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
}

function dateKeyToTs(k) {
  if (!k || k === "unknown") return -1;
  const t = new Date(k).getTime();
  return Number.isFinite(t) ? t : -1;
}

export default function FieldHistoryPanel({ user, fieldId, seasons, onToast, reloadKey }) {
  const loadSeq = useSeq();

  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [list, setList] = useState([]);

  // filters
  const [seasonFilter, setSeasonFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [q, setQ] = useState("");

  // 🔧 якщо треба дебаг — включи true
  const SHOW_RAW_JSON = false;

  function toast(type, text) {
    if (onToast) onToast(type, text);
  }

  const seasonIdToYear = useMemo(() => {
    const map = new Map();
    (Array.isArray(seasons) ? seasons : []).forEach((s) => {
      if (s?.id != null) map.set(String(s.id), String(s.year ?? ""));
    });
    return map;
  }, [seasons]);

  async function loadHistory() {
    if (!user || !fieldId) return;

    const seq = loadSeq.next();
    setLoading(true);
    setApiError(null);

    try {
      // ✅ 1) /history
      let res = await apiFetch(user, `/api/fields/${fieldId}/history`, { method: "GET" });
      if (!loadSeq.isLatest(seq)) return;

      // ✅ 2) fallback /events
      if (res.status === 404) {
        res = await apiFetch(user, `/api/fields/${fieldId}/events`, { method: "GET" });
        if (!loadSeq.isLatest(seq)) return;
      }

      if (!res.ok) {
        const t = await safeReadError(res);
        setApiError(t || `Помилка історії (${res.status})`);
        setList([]);
        return;
      }

      const data = await res.json();
      const arr = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
      setList(Array.isArray(arr) ? arr : []);
    } catch {
      if (!loadSeq.isLatest(seq)) return;
      setApiError("Не вдалося завантажити історію");
      setList([]);
    } finally {
      if (loadSeq.isLatest(seq)) setLoading(false);
    }
  }

  useEffect(() => {
    if (!user || !fieldId) return;
    loadHistory().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, fieldId, reloadKey]);

  const typeOptions = useMemo(() => {
    const set = new Set();
    (Array.isArray(list) ? list : []).forEach((x) => {
      if (x?.type) set.add(String(x.type));
    });
    const arr = Array.from(set).sort();
    return arr.length ? arr : TYPE_LABELS.map((x) => x.value);
  }, [list]);

  const seasonOptions = useMemo(() => {
    const arr = Array.isArray(seasons) ? [...seasons] : [];
    arr.sort((a, b) => Number(b?.year || 0) - Number(a?.year || 0));
    return arr;
  }, [seasons]);

  const filtered = useMemo(() => {
    const query = String(q || "").trim().toLowerCase();

    return (Array.isArray(list) ? list : [])
      .filter((ev) => {
        if (seasonFilter !== "ALL") {
          const sid = ev?.seasonId ?? ev?.fieldSeasonId ?? ev?.season_id ?? ev?.fieldSeason?.id;
          if (String(sid ?? "") !== String(seasonFilter)) return false;
        }
        if (typeFilter !== "ALL") {
          if (String(ev?.type ?? "") !== String(typeFilter)) return false;
        }
        if (query) {
          const blob = `${ev?.title ?? ""} ${ev?.description ?? ""} ${ev?.dataJson ?? ""} ${ev?.type ?? ""}`.toLowerCase();
          if (!blob.includes(query)) return false;
        }
        return true;
      })
      .sort((a, b) => (getEventAt(b) || 0) - (getEventAt(a) || 0)); // ✅ newest first
  }, [list, seasonFilter, typeFilter, q]);

  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((ev) => {
      const k = groupKeyByDate(ev);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(ev);
    });

    // ✅ newest day first, "unknown" last
    const keys = Array.from(map.keys()).sort((a, b) => (dateKeyToTs(b) || 0) - (dateKeyToTs(a) || 0));

    return keys.map((k) => {
      const items = (map.get(k) || []).slice().sort((a, b) => (getEventAt(b) || 0) - (getEventAt(a) || 0)); // ✅ newest in day first
      return { date: k, items };
    });
  }, [filtered]);

  return (
    <div className="panel" style={{ marginTop: 14 }}>
      <div className="sectionHead">
        <div>
          <div className="sectionTitle">Історія поля</div>
          <div className="sectionSub">Зверху — останні оновлення (хто / коли / що саме)</div>
        </div>

        <div className="sectionRight">
          <button className="btn" type="button" onClick={() => loadHistory().catch(() => {})}>
            ⟳ Оновити
          </button>
        </div>
      </div>

      <style>{`
        .sectionHead{ display:flex; gap:12px; justify-content:space-between; align-items:flex-start; flex-wrap:wrap; }
        .sectionTitle{ font-weight: 980; font-size: 18px; letter-spacing: .01em; }
        .sectionSub{ margin-top: 2px; opacity: .72; font-weight: 700; font-size: 13px; }
        .sectionRight{ display:flex; gap:10px; flex-wrap:wrap; }

        .fg{ display:flex; flex-direction:column; gap:8px; }
        .lbl{ font-size: 12px; font-weight: 900; letter-spacing: .02em; color: rgba(255,255,255,0.72); text-transform: uppercase; display:flex; align-items:center; gap:8px; }
        .inputXL, .selectXL{
          width: 100%;
          min-height: 46px;
          padding: 12px 12px;
          border-radius: 16px;
          border: 1px solid rgba(255,255,255,0.10);
          background: linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03));
          color: rgba(255,255,255,0.92);
          outline: none;
        }

        .histFilters{
          margin-top: 12px;
          display:grid;
          grid-template-columns: 1fr 1fr 1.2fr auto;
          gap: 10px;
          align-items: center;
        }
        @media (max-width: 980px){ .histFilters{ grid-template-columns: 1fr 1fr; } }
        @media (max-width: 520px){ .histFilters{ grid-template-columns: 1fr; } }

        .histList{ margin-top: 12px; display:grid; gap: 12px; }
        .histDay{
          border-radius: 18px;
          border: 1px solid rgba(255,255,255,0.10);
          background: rgba(255,255,255,0.03);
          overflow: hidden;
        }
        .histDayHead{
          padding: 10px 12px;
          display:flex;
          justify-content:space-between;
          align-items:center;
          border-bottom: 1px solid rgba(255,255,255,0.08);
          background: radial-gradient(900px 240px at 0% 0%, rgba(34,197,94,0.08), transparent 55%), rgba(0,0,0,0.12);
        }
        .histDayTitle{ font-weight: 980; letter-spacing: .01em; }
        .histDayCount{ opacity: .72; font-weight: 900; font-size: 13px; }
        .histItems{ padding: 12px; display:grid; gap: 10px; }

        .histItem{
          border-radius: 16px;
          border: 1px solid rgba(255,255,255,0.10);
          background: rgba(0,0,0,0.18);
          padding: 12px;
          display:grid;
          gap: 10px;
        }

        .histTop{
          display:flex;
          gap:10px;
          align-items:flex-start;
          justify-content:space-between;
          flex-wrap: wrap;
        }
        .histTitle{
          font-weight: 980;
          font-size: 15px;
          line-height: 1.2;
        }

        .histMeta{
          display:flex;
          gap:10px;
          flex-wrap: wrap;
          align-items:center;
          opacity: .96;
        }
        .histPill{
          display:inline-flex; align-items:center; gap:8px;
          padding: 7px 10px;
          border-radius: 999px;
          border: 1px solid rgba(255,255,255,0.12);
          background: rgba(0,0,0,0.18);
          font-weight: 900;
          color: rgba(255,255,255,0.88);
          font-size: 13px;
        }
        .histPill[data-tone="ok"]{ background: rgba(34,197,94,0.14); border-color: rgba(34,197,94,0.35); }
        .histPill[data-tone="ok2"]{ background: rgba(34,197,94,0.10); border-color: rgba(34,197,94,0.28); }
        .histPill[data-tone="warn"]{ background: rgba(251,191,36,0.14); border-color: rgba(251,191,36,0.35); }
        .histPill[data-tone="info"]{ background: rgba(59,130,246,0.16); border-color: rgba(59,130,246,0.40); }
        .histPill[data-tone="done"]{ background: rgba(34,197,94,0.18); border-color: rgba(34,197,94,0.48); }
        .histPill[data-tone="muted"]{ background: rgba(255,255,255,0.04); }

        .histDesc{
          opacity: .86;
          line-height: 1.35;
          white-space: pre-wrap;
        }

        .statusChangeBox{
          border-radius: 14px;
          border: 1px solid rgba(255,255,255,0.10);
          background: rgba(255,255,255,0.03);
          padding: 10px;
          display:flex;
          gap:10px;
          align-items:center;
          flex-wrap: wrap;
        }
        .statusChangeTitle{
          font-weight: 950;
          opacity: .92;
        }

        .histJson{
          opacity: .75;
          font-size: 12px;
          line-height: 1.35;
          border-radius: 12px;
          border: 1px dashed rgba(255,255,255,0.14);
          padding: 10px;
          background: rgba(0,0,0,0.14);
          overflow:auto;
        }

        .histEmpty{
          margin-top: 12px;
          border-radius: 16px;
          padding: 12px;
          border: 1px dashed rgba(255,255,255,0.14);
          background: rgba(0,0,0,0.14);
          opacity: .88;
          line-height: 1.35;
        }
      `}</style>

      {/* Filters */}
      <div className="histFilters">
        <div className="fg">
          <div className="lbl">Сезон</div>
          <select className="selectXL" value={seasonFilter} onChange={(e) => setSeasonFilter(e.target.value)} disabled={loading}>
            <option value="ALL">Всі сезони</option>
            {seasonOptions.map((s) => (
              <option key={String(s.id)} value={String(s.id)}>
                {s.year ? `Сезон ${s.year}` : `Season #${s.id}`}
              </option>
            ))}
          </select>
        </div>

        <div className="fg">
          <div className="lbl">Тип</div>
          <select className="selectXL" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} disabled={loading}>
            <option value="ALL">Всі типи</option>
            {typeOptions.map((t) => (
              <option key={t} value={t}>
                {labelFrom(TYPE_LABELS, t)}
              </option>
            ))}
          </select>
        </div>

        <div className="fg">
          <div className="lbl">Пошук</div>
          <input className="inputXL" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Напр: статус, посів, добрива..." maxLength={120} disabled={loading} />
        </div>

        <div className="fg">
          <div className="lbl">Знайдено</div>
          <div className="histPill" data-tone="muted" style={{ justifyContent: "center", height: 46 }}>
            {filtered.length}
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ marginTop: 12, fontWeight: 850, opacity: 0.8 }}>Завантажую історію...</div>
      ) : apiError ? (
        <div className="histEmpty">⚠️ {apiError}</div>
      ) : grouped.length === 0 ? (
        <div className="histEmpty">
          Історія поки пуста.
        </div>
      ) : (
        <div className="histList">
          {grouped.map((g) => (
            <div className="histDay" key={g.date}>
              <div className="histDayHead">
                <div className="histDayTitle">{g.date === "unknown" ? "Без дати" : fmtDateUA(g.date)}</div>
                <div className="histDayCount">{g.items.length} подій</div>
              </div>

              <div className="histItems">
                {g.items.map((ev) => {
                  const type = String(ev?.type ?? "OTHER");
                  const tone = toneForType(type);

                  const sid = ev?.seasonId ?? ev?.fieldSeasonId;
                  const seasonYear = sid != null ? seasonIdToYear.get(String(sid)) : "";
                  const seasonText = seasonYear ? `Сезон ${seasonYear}` : sid != null ? `Season #${sid}` : "—";

                  const actor = humanActor(ev);
                  const createdAt = fmtDateTimeUA(ev?.createdAt);

                  const meta = safeJsonParse(ev?.dataJson);
                  const fromRaw = meta?.from ?? meta?.old ?? meta?.prev ?? null;
                  const toRaw = meta?.to ?? meta?.next ?? meta?.new ?? null;

                  const showChange = type === "STATUS_CHANGE" && (fromRaw || toRaw);

                  // ✅ красивий заголовок
                  let title = clampStr(ev?.title ?? "", 140);
                  if (!title) {
                    title = showChange ? "Зміна статусу сезону" : "Подія";
                  }
                  if (showChange) {
                    title = "Зміна статусу сезону";
                  }

                  const desc = String(ev?.description ?? "").trim();

                  // ✅ переклад статусів
                  const fromLabel = fromRaw ? seasonFieldLabel(fromRaw) : "—";
                  const toLabel = toRaw ? seasonFieldLabel(toRaw) : "—";

                  const fromTone = statusTone(fromRaw);
                  const toTone = statusTone(toRaw);

                  return (
                    <div className="histItem" key={String(ev?.id ?? `${type}-${getEventAt(ev)}-${title}`)}>
                      <div className="histTop">
                        <div className="histTitle">{title}</div>

                        <div className="histMeta">
                          <span className="histPill" data-tone={tone}>
                            {labelFrom(TYPE_LABELS, type)}
                          </span>
                          <span className="histPill" data-tone="muted">
                            {seasonText}
                          </span>
                          <span className="histPill" data-tone="muted">
                            👤 {actor}
                          </span>
                          <span className="histPill" data-tone="muted">
                            🕒 {createdAt}
                          </span>
                        </div>
                      </div>

                      {showChange ? (
                        <div className="statusChangeBox">
                          <div className="statusChangeTitle">Було</div>
                          <span className="histPill" data-tone={fromTone}>
                            {fromLabel}
                          </span>

                          <span style={{ opacity: 0.7, fontWeight: 900 }}>→</span>

                          <div className="statusChangeTitle">Стало</div>
                          <span className="histPill" data-tone={toTone}>
                            {toLabel}
                          </span>
                        </div>
                      ) : null}

                      {desc ? <div className="histDesc">{desc}</div> : null}

                      {SHOW_RAW_JSON && ev?.dataJson ? (
                        <div className="histJson">
                          <b>dataJson:</b> {String(ev.dataJson)}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
